import { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportIgrejasDialog({ state, setState, onImported }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const normalizeErrors = (detail) => {
    if (Array.isArray(detail?.errors)) return detail.errors;
    if (Array.isArray(detail)) return detail.map(item => item?.msg || JSON.stringify(item));
    if (typeof detail === 'string') return [detail];
    if (detail?.message) return [detail.message];
    return ['Erro ao importar igrejas'];
  };

  const isValidDate = (value) => {
    const clean = (value || '').trim();
    if (!clean) return true;
    const iso = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const br = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const parts = iso ? [iso[1], iso[2], iso[3]] : br ? [br[3], br[2].padStart(2, '0'), br[1].padStart(2, '0')] : null;
    if (!parts) return false;
    const [year, month, day] = parts.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  };

  const validateClientText = () => {
    const delimiter = state.delimiter;
    const rawLines = (state.text || '').split('\n').map(line => line.trim()).filter(Boolean);
    const header = ['nome', 'código', 'cidade', 'estado', 'nome do cooperador', 'email', 'whatsapp', 'data de nascimento', 'data de apresentação'];
    const foundErrors = [];
    rawLines.forEach((line, index) => {
      const lineNumber = index + 1;
      const parts = line.split(delimiter).map(part => part.trim());
      if (lineNumber === 1 && JSON.stringify(parts.map(part => part.toLowerCase())) === JSON.stringify(header)) return;
      if (parts.length !== 9) {
        foundErrors.push(`Linha ${lineNumber}: esperado 9 campos, encontrado ${parts.length}`);
        return;
      }
      const [churchName, , , , adminName, email, whatsapp, birthDate, presentationDate] = parts;
      if (!churchName) foundErrors.push(`Linha ${lineNumber}: Nome da igreja é obrigatório`);
      if (!adminName) foundErrors.push(`Linha ${lineNumber}: Nome do cooperador é obrigatório`);
      if (!email) foundErrors.push(`Linha ${lineNumber}: Email é obrigatório`);
      if (!whatsapp.replace(/\D/g, '')) foundErrors.push(`Linha ${lineNumber}: WhatsApp é obrigatório`);
      if (!isValidDate(birthDate)) foundErrors.push(`Linha ${lineNumber}: Data de Nascimento inválida`);
      if (!isValidDate(presentationDate)) foundErrors.push(`Linha ${lineNumber}: Data de Apresentação inválida`);
    });
    return foundErrors;
  };

  const importData = async () => {
    setErrors([]);
    setErrorMessage('');
    if (!state.text.trim()) { toast.error('Cole a lista de igrejas'); return; }
    const clientErrors = validateClientText();
    if (clientErrors.length > 0) {
      const message = 'Corrija as linhas inválidas antes de importar';
      setErrorMessage(message);
      setErrors(clientErrors);
      toast.error(message);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/gestor/churches/import`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delimiter: state.delimiter, text: state.text }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = data.detail;
        const inlineErrors = normalizeErrors(detail);
        const message = detail?.message || inlineErrors[0] || 'Erro ao importar igrejas';
        setErrors(inlineErrors);
        setErrorMessage(message);
        toast.error(message);
        return;
      }
      toast.success(`Importação concluída: ${data.created_churches || 0} igrejas e ${data.created_admins || 0} cooperadores`);
      setState(prev => ({ ...prev, open: false, text: '' }));
      onImported();
    } catch (err) {
      const message = err.message || 'Erro ao importar igrejas';
      setErrorMessage(message);
      setErrors([message]);
      toast.error(message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={(open) => { setState(prev => ({ ...prev, open })); if (!open) { setErrors([]); setErrorMessage(''); } }}>
      <DialogContent className="max-w-4xl" data-testid="igrejas-import-modal">
        <DialogHeader>
          <DialogTitle>Importar Igrejas</DialogTitle>
          <DialogDescription>Cole uma linha por igreja no formato Nome|Código|Cidade|Estado|Nome do Cooperador|Email|WhatsApp|Data de Nascimento|Data de Apresentação. Se houver erro, nada será importado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Divisor</Label>
              <select value={state.delimiter} onChange={(e) => setState(prev => ({ ...prev, delimiter: e.target.value }))} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="igrejas-import-delimiter">
                <option value="|">|</option><option value=";">;</option><option value=",">,</option>
              </select>
            </div>
            <div className="sm:col-span-2 flex items-end">
              <p className="text-xs text-slate-500">Exemplo: Central{state.delimiter}001{state.delimiter}São Paulo{state.delimiter}SP{state.delimiter}João Silva{state.delimiter}joao@email.com{state.delimiter}11999999999{state.delimiter}01/01/1980{state.delimiter}15/03/2024</p>
            </div>
          </div>
          <div>
            <Label>Lista de igrejas</Label>
            <textarea value={state.text} onChange={(e) => setState(prev => ({ ...prev, text: e.target.value }))} className="mt-1 min-h-[300px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm font-mono" placeholder={`Nome${state.delimiter}Código${state.delimiter}Cidade${state.delimiter}Estado${state.delimiter}Nome do Cooperador${state.delimiter}Email${state.delimiter}WhatsApp${state.delimiter}Data de Nascimento${state.delimiter}Data de Apresentação\nCentral${state.delimiter}001${state.delimiter}São Paulo${state.delimiter}SP${state.delimiter}João Silva${state.delimiter}joao@email.com${state.delimiter}11999999999${state.delimiter}01/01/1980${state.delimiter}15/03/2024`} data-testid="igrejas-import-textarea" />
          </div>
          {(errors.length > 0 || errorMessage) && <div className="max-h-40 overflow-auto rounded-md border border-red-200 bg-red-50 p-3" data-testid="igrejas-import-errors">{errorMessage && <p className="mb-1 text-xs font-semibold text-red-800">{errorMessage}</p>}{errors.map((err, idx) => <p key={idx} className="text-xs text-red-700">{err}</p>)}</div>}
          <Button onClick={importData} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="igrejas-import-submit-button">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-1.5" />}Importar igrejas
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}